import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { sendEmail } from '../../../lib/emailService';

const FAQS = [
  {
    q: 'How long does the review process take?',
    a: 'The full review process typically takes 3–5 business days. You will receive email updates at each stage of the process.',
  },
  {
    q: 'What documents are required for the application?',
    a: 'You need a valid government-issued photo ID (passport, national ID, or driver\'s license), a selfie for liveness verification, and proof of your social media presence.',
  },
  {
    q: 'Can I reapply if my application is rejected?',
    a: 'Yes. You may reapply after 30 days from the rejection date. We recommend improving your SocialCents Score and ensuring all documents are valid before reapplying.',
  },
  {
    q: 'What is the SocialCents Score used for?',
    a: 'Your SocialCents Score reflects your engagement, reach, and reliability as a creator. A higher score improves your eligibility and may unlock better card benefits.',
  },
];

const ContactSupportSection = ({ application, userEmail }) => {
  const [openFaq, setOpenFaq] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState('');

  const refId = application?.id?.slice(0, 8)?.toUpperCase() || 'N/A';

  const handleMessageSupport = async () => {
    if (!userEmail) return;
    setSending(true);
    setSendError('');
    try {
      const result = await sendEmail(userEmail, 'transactional', {
        name: application?.full_name || 'Applicant',
        subject: `Support Request — Card Application #${refId}`,
        message: `I need assistance with my SocialCents Card application.\n\nReference ID: ${refId}\nCurrent Status: ${application?.status || 'pending'}\n\nPlease review my application and provide an update.`,
      });
      if (result?.success) {
        setSent(true);
      } else {
        setSendError('Failed to send message. Please try again.');
      }
    } catch (err) {
      setSendError('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-6"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}
    >
      <div className="flex items-center gap-2 mb-5">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg,#0d9488,#1d4ed8)' }}
        >
          <Icon name="HeadphonesIcon" size={13} color="#fff" />
        </div>
        <h2 className="text-sm font-semibold text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Need Help?</h2>
      </div>
      {/* Support message button */}
      <div
        className="flex items-center justify-between p-4 rounded-xl mb-5"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <div>
          <p className="text-sm font-medium text-white" style={{ fontFamily: 'DM Sans, sans-serif' }}>Message Support</p>
          <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Ref: #{refId} · Typical response: 24–48 hrs</p>
        </div>
        {sent ? (
          <div
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold"
            style={{ background: 'rgba(16,185,129,0.12)', color: '#10b981', border: '1px solid rgba(16,185,129,0.25)' }}
          >
            <Icon name="CheckCircle" size={13} />
            Sent!
          </div>
        ) : (
          <button
            onClick={handleMessageSupport}
            disabled={sending}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg,#0d9488,#1d4ed8)', color: '#fff' }}
          >
            {sending ? (
              <><Icon name="Loader2" size={13} className="animate-spin" />Sending...</>
            ) : (
              <><Icon name="Mail" size={13} />Message Support</>
            )}
          </button>
        )}
      </div>
      {sendError && (
        <p className="text-xs mb-4" style={{ color: '#ef4444' }}>{sendError}</p>
      )}
      {/* Response time indicator */}
      <div
        className="flex items-center gap-2 p-3 rounded-xl mb-5"
        style={{ background: 'rgba(20,184,166,0.06)', border: '1px solid rgba(20,184,166,0.15)' }}
      >
        <Icon name="Clock" size={13} color="#14B8A6" />
        <span className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>Expected response time: <strong style={{ color: 'rgba(255,255,255,0.75)' }}>24–48 business hours</strong></span>
      </div>
      {/* FAQ Accordion */}
      <div>
        <p className="text-xs font-semibold mb-3" style={{ color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Frequently Asked Questions</p>
        <div className="space-y-2">
          {FAQS?.map((faq, i) => (
            <div
              key={i}
              className="rounded-xl overflow-hidden"
              style={{ border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between gap-3 p-3.5 text-left transition-all"
                style={{ background: openFaq === i ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)' }}
              >
                <span className="text-xs font-medium" style={{ color: 'rgba(255,255,255,0.75)', fontFamily: 'DM Sans, sans-serif' }}>{faq?.q}</span>
                <Icon
                  name={openFaq === i ? 'ChevronUp' : 'ChevronDown'}
                  size={14}
                  color="rgba(255,255,255,0.35)"
                  className="flex-shrink-0 transition-transform"
                />
              </button>
              {openFaq === i && (
                <div
                  className="px-3.5 pb-3.5"
                  style={{ background: 'rgba(255,255,255,0.02)' }}
                >
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.45)', lineHeight: '1.6' }}>{faq?.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ContactSupportSection;
